import { GameScene } from '../GameScene';
import { PassiveTreePanel } from '../ui/PassiveTreePanel';
import type { CharacterRaceId } from '@shared/types/character';
import {
  ClientMessageType,
  type SkillTreeSyncMessage,
  type SkillNodeAllocatedMessage,
  type SkillTreeResetMessage,
  type AllocateSkillNodeMessage,
  type RequestSkillTreeMessage,
  type ResetSkillTreeMessage,
} from '@shared/types/messages';

/**
 * UI del Árbol de Espíritu (pasivas) del cliente: crea/togglea el panel (createPassiveTreePanel/
 * togglePassiveTreePanel) y aplica los mensajes de sync del server (handleSkillTreeSync /
 * handleSkillNodeAllocated / handleSkillTreeReset). Es el árbol de ESPÍRITU (esprit), NO el
 * skills-system OSRS. Extraído de GameScene (god-object). Accede al scene vía `this.scene.*`. Ver
 * docs/SKILL_TREE.md.
 */
export class PassiveTreeUiController {
  constructor(private scene: GameScene) {}

  public createPassiveTreePanel(): void {
    this.scene.passiveTreePanel = new PassiveTreePanel(this.scene, {
      onClose: () => {
        // El panel se cerró
      },
      onAllocateNode: (nodeId: string) => {
        if (this.scene.colyseusClient) {
          const msg: AllocateSkillNodeMessage = {
            type: ClientMessageType.ALLOCATE_SKILL_NODE,
            nodeId,
          };
          this.scene.colyseusClient.send(msg);
        }
      },
      onResetTree: () => {
        if (this.scene.colyseusClient) {
          const msg: ResetSkillTreeMessage = {
            type: ClientMessageType.RESET_SKILL_TREE,
          };
          this.scene.colyseusClient.send(msg);
        }
      },
      onRequestSync: () => {
        if (this.scene.colyseusClient) {
          const msg: RequestSkillTreeMessage = {
            type: ClientMessageType.REQUEST_SKILL_TREE,
          };
          this.scene.colyseusClient.send(msg);
        }
      },
    });
    
    // Inicializar con la raza del jugador actual
    const playerState = this.scene.localSessionId ? this.scene.room?.state.players.get(this.scene.localSessionId) : null;
    if (playerState) {
      const race = playerState.race as CharacterRaceId;
      this.scene.passiveTreePanel.initialize(race);
    } else {
      // Si no hay jugador, usar humano por defecto
      this.scene.passiveTreePanel.initialize('human');
    }
  }

  public togglePassiveTreePanel(): void {
    if (!this.scene.passiveTreePanel) {
      this.createPassiveTreePanel();
    }
    
    // Actualizar la raza antes de mostrar
    const playerState = this.scene.localSessionId ? this.scene.room?.state.players.get(this.scene.localSessionId) : null;
    if (playerState && this.scene.passiveTreePanel) {
      const race = playerState.race as CharacterRaceId;
      this.scene.passiveTreePanel.setPlayerRace(race);
    }
    
    this.scene.passiveTreePanel?.toggle();
  }

  /** Puntos disponibles del último sync (para detectar cuando GANÁS un punto y sonar el SFX). */
  private lastAvailablePoints: number | null = null;

  public handleSkillTreeSync(message: SkillTreeSyncMessage): void {
    if (this.scene.passiveTreePanel) {
      this.scene.passiveTreePanel.syncFromServer(message.data);
    }
    // SFX al GANAR un punto de Espíritu (los puntos disponibles subieron). No en el primer sync ni
    // al gastar. 2D. Ver docs/SOUND_AND_MUSIC.md.
    const now = message.data.availablePoints;
    if (this.lastAvailablePoints !== null && now > this.lastAvailablePoints) {
      this.scene.audio.play('sfx_passive_point');
    }
    this.lastAvailablePoints = now;
  }

  public handleSkillNodeAllocated(message: SkillNodeAllocatedMessage): void {
    if (this.scene.passiveTreePanel) {
      this.scene.passiveTreePanel.confirmNodeAllocation(message.nodeId, message.remainingPoints);
    }
  }

  public handleSkillTreeReset(message: SkillTreeResetMessage): void {
    if (this.scene.passiveTreePanel) {
      this.scene.passiveTreePanel.handleTreeReset(message.availablePoints);
    }
  }

}
